import { useState, useEffect } from "react";
import axios from "axios";
import {
  Upload, Package, Search, Edit, Trash2, Check, X,
  AlertCircle, FileText, Users, ArrowRightLeft
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PackageManagement = () => {
  const [importedPackages, setImportedPackages] = useState([]);
  const [customerPackages, setCustomerPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingPackage, setEditingPackage] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", description: "", tracking_number: "" });

  const token = localStorage.getItem("token");
  const axiosAuth = axios.create({
    headers: { Authorization: `Bearer ${token}` },
  });

  useEffect(() => {
    fetchPackages();
  }, []);

  const fetchPackages = async () => {
    try {
      const [importedRes, customerRes] = await Promise.all([
        axiosAuth.get(`${API}/packages/admin/imported`),
        axiosAuth.get(`${API}/packages/admin/customer-packages`),
      ]);
      setImportedPackages(importedRes.data);
      setCustomerPackages(customerRes.data);
    } catch (err) {
      toast.error("Failed to load packages");
    } finally {
      setLoading(false);
    }
  };

  const handleCsvUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.endsWith(".csv")) {
      toast.error("Please upload a CSV file");
      return;
    }

    setUploading(true);
    setImportResults(null);
    const formData = new FormData();
    formData.append("file", file);

    try {
      const res = await axiosAuth.post(`${API}/packages/admin/import`, formData, {
        headers: { "Content-Type": "multipart/form-data", Authorization: `Bearer ${token}` },
      });
      setImportResults(res.data);
      toast.success(`Imported ${res.data.total_imported} packages, ${res.data.matched_count} matched`);
      fetchPackages();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to import CSV");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const handleDeleteImported = async (id) => {
    if (!window.confirm("Delete this imported package?")) return;
    try {
      await axiosAuth.delete(`${API}/packages/admin/imported/${id}`);
      toast.success("Package deleted");
      setImportedPackages(importedPackages.filter((p) => p.id !== id));
    } catch (err) {
      toast.error("Failed to delete");
    }
  };

  const openEditDialog = (pkg) => {
    setEditingPackage(pkg);
    setEditForm({
      name: pkg.name,
      description: pkg.description || "",
      tracking_number: pkg.tracking_number,
    });
    setEditDialogOpen(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      const res = await axiosAuth.put(
        `${API}/packages/admin/customer-packages/${editingPackage.id}`,
        editForm
      );
      toast.success("Package updated");
      setCustomerPackages(customerPackages.map((p) => (p.id === editingPackage.id ? res.data : p)));
      setEditDialogOpen(false);
      fetchPackages();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update");
    }
  };

  const statusBadge = (status) => {
    const map = {
      PENDING: "bg-amber-500",
      MATCHED: "bg-green-500",
      CANCELLED: "bg-red-500",
    };
    return (
      <Badge className={`${map[status] || "bg-slate-500"} text-white text-[10px] uppercase tracking-widest`}>
        {status}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* CSV Upload Section */}
      <Card className="border-slate-200 border-l-4 border-l-amber-500">
        <CardHeader className="pb-3">
          <CardTitle className="font-chivo uppercase tracking-tight flex items-center gap-2 text-base">
            <Upload className="w-5 h-5 text-amber-500" />
            Import Packages (CSV)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="flex-1">
              <p className="text-slate-500 text-sm mb-2">
                Upload a CSV with columns: <code className="bg-slate-100 px-1.5 py-0.5 rounded text-xs font-mono">Date, Carrier, Description, Weight, Tracking #</code>
              </p>
              <label
                className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold text-xs uppercase tracking-widest cursor-pointer transition-colors"
                data-testid="csv-upload-btn"
              >
                <Upload className="w-4 h-4" />
                {uploading ? "Uploading..." : "Choose CSV File"}
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleCsvUpload}
                  className="hidden"
                  disabled={uploading}
                  data-testid="csv-upload-input"
                />
              </label>
            </div>
          </div>

          {/* Import Results */}
          {importResults && (
            <div className="mt-4 p-4 bg-slate-50 border border-slate-200 rounded" data-testid="import-results">
              <div className="flex items-center gap-6 mb-3">
                <span className="text-sm font-mono">
                  <strong className="text-slate-900">{importResults.total_imported}</strong> imported
                </span>
                <span className="text-sm font-mono text-green-600">
                  <strong>{importResults.matched_count}</strong> matched
                </span>
                <span className="text-sm font-mono text-blue-600">
                  <strong>{importResults.shipments_created}</strong> shipments created
                </span>
              </div>
              <div className="space-y-1 max-h-40 overflow-y-auto">
                {importResults.packages.map((pkg, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs">
                    {pkg.status === "matched" ? (
                      <Check className="w-3 h-3 text-green-500 shrink-0" />
                    ) : pkg.status === "duplicate" ? (
                      <AlertCircle className="w-3 h-3 text-amber-500 shrink-0" />
                    ) : (
                      <Package className="w-3 h-3 text-slate-400 shrink-0" />
                    )}
                    <span className="font-mono text-slate-700">{pkg.tracking_number}</span>
                    <span className="text-slate-500">— {pkg.message}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Package Lists */}
      <Tabs defaultValue="customer" className="space-y-4">
        <TabsList className="bg-slate-200">
          <TabsTrigger value="customer" data-testid="customer-packages-tab">
            <Users className="w-4 h-4 mr-1.5" />
            Customer Packages ({customerPackages.length})
          </TabsTrigger>
          <TabsTrigger value="imported" data-testid="imported-packages-tab">
            <FileText className="w-4 h-4 mr-1.5" />
            Imported Packages ({importedPackages.length})
          </TabsTrigger>
        </TabsList>

        {/* Customer Pre-registered Packages */}
        <TabsContent value="customer">
          {customerPackages.length === 0 ? (
            <Card className="border-slate-200">
              <CardContent className="p-8 text-center">
                <Users className="w-12 h-12 text-slate-300 mx-auto mb-3" strokeWidth={1} />
                <p className="text-slate-500 text-sm">No customer pre-registered packages yet</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3" data-testid="customer-packages-list">
              {customerPackages.map((pkg) => (
                <Card key={pkg.id} className="border-slate-200 hover:border-slate-300 transition-colors">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex items-center gap-3 min-w-0">
                        <Package className="w-5 h-5 text-slate-400 shrink-0" />
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-mono font-bold text-slate-900 text-sm" data-testid={`cust-pkg-tracking-${pkg.id}`}>
                              {pkg.tracking_number}
                            </span>
                            {statusBadge(pkg.status)}
                          </div>
                          <p className="text-slate-700 text-sm font-medium truncate">{pkg.name}</p>
                          <p className="text-slate-500 text-xs truncate">
                            {pkg.customer_email} {pkg.description && `— ${pkg.description}`}
                          </p>
                          {pkg.matched_shipment_id && (
                            <p className="text-green-600 text-xs mt-1 flex items-center gap-1">
                              <ArrowRightLeft className="w-3 h-3" />
                              Shipment created
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        {pkg.status === "PENDING" && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditDialog(pkg)}
                            className="text-blue-500 hover:text-blue-700 hover:bg-blue-50"
                            data-testid={`edit-cust-pkg-${pkg.id}`}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Imported Packages */}
        <TabsContent value="imported">
          {importedPackages.length === 0 ? (
            <Card className="border-slate-200">
              <CardContent className="p-8 text-center">
                <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" strokeWidth={1} />
                <p className="text-slate-500 text-sm">No imported packages yet. Upload a CSV above.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3" data-testid="imported-packages-list">
              {importedPackages.map((pkg) => (
                <Card key={pkg.id} className="border-slate-200 hover:border-slate-300 transition-colors">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex items-center gap-3 min-w-0">
                        <FileText className="w-5 h-5 text-slate-400 shrink-0" />
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-mono font-bold text-slate-900 text-sm">
                              {pkg.tracking_number}
                            </span>
                            {pkg.matched ? (
                              <Badge className="bg-green-500 text-white text-[10px] uppercase tracking-widest">Matched</Badge>
                            ) : (
                              <Badge className="bg-slate-400 text-white text-[10px] uppercase tracking-widest">Unmatched</Badge>
                            )}
                          </div>
                          <p className="text-slate-700 text-sm truncate">
                            {pkg.carrier} — {pkg.description || "No description"}
                          </p>
                          <p className="text-slate-400 text-xs">
                            {pkg.date} {pkg.weight ? `| ${pkg.weight} lbs` : ""}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        {!pkg.matched && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteImported(pkg.id)}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            data-testid={`delete-imported-${pkg.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Edit Customer Package Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-chivo font-bold uppercase tracking-tight">
              Edit Customer Package
            </DialogTitle>
          </DialogHeader>
          {editingPackage && (
            <form onSubmit={handleEditSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest">Package Name</Label>
                <Input
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  required
                  data-testid="edit-pkg-name"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest">Description</Label>
                <Input
                  value={editForm.description}
                  onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                  data-testid="edit-pkg-description"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest">Tracking Number</Label>
                <Input
                  value={editForm.tracking_number}
                  onChange={(e) => setEditForm({ ...editForm, tracking_number: e.target.value })}
                  required
                  data-testid="edit-pkg-tracking"
                />
                <p className="text-xs text-slate-500">Fix typos here. After saving, auto-matching will re-check.</p>
              </div>
              <div className="flex gap-3">
                <Button
                  type="submit"
                  className="flex-1 bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold uppercase tracking-widest text-xs"
                  data-testid="save-edit-pkg-btn"
                >
                  Save Changes
                </Button>
                <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>
                  Cancel
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PackageManagement;
